import type { UserDoc } from './types';

/**
 * Lease §11.2: a lessee provides a certificate of insurance "before Lessee's
 * first flight as sole pilot in command, and in any event within fourteen (14)
 * days of the Effective Date… Until such certificate is provided, Lessee shall
 * not operate the Aircraft except on dual instruction flights with a flight
 * instructor named and approved under Lessor's Policy."
 *
 * This is deliberately NOT an enforcement gate. Bookings carry no solo/dual
 * flag, so any hard block would rest on a self-declared checkbox — honour system
 * with extra steps, plus a new place to be untruthful. What protects the owner
 * is the record: the lease, the initialed acknowledgment, and this notice on
 * screen every time the pilot books.
 */
export const SOLO_GATE_DAYS = 14;

/**
 * MUTED 8/23/2026 at Casey's direction: the §11.2 notice stays OFF in the pilot-facing
 * app until he has emailed each renter himself. Finding out mid-app that you're
 * restricted to dual instruction should not be how a renter first hears it.
 *
 * This gates the PILOT views only. The owner's lessee cards are unaffected — Casey
 * still needs to see exactly who is missing a certificate and how overdue they are.
 *
 * Flip to `true` after the emails go out; nothing else needs to change.
 */
export const SHOW_SOLO_NOTICE_TO_PILOTS = false;

/** Inside this many days of expiry the owner's views turn amber. */
export const INSURANCE_EXPIRING_DAYS = 30;

export type InsuranceState = 'none' | 'expired' | 'expiring' | 'current';

/**
 * What the policy on file actually is TODAY. A carrier, a number and a date are
 * not a policy — a date in the past is a lapsed one, and the app used to read
 * that as "on file" (Casey, 10/2/2026: it must not just *seem* clear). Expiry is
 * counted through the end of the expiration day, local time, matching how the
 * certificate reads ("to 01 Nov 2026").
 */
export function insuranceStatus(
  u: Pick<UserDoc, 'insurance'> | null | undefined,
  ref: Date = new Date(),
): { state: InsuranceState; days: number | null; exp: string | null } {
  const i = u?.insurance;
  if (!(i?.carrier && i?.policyNumber && i?.expirationDate)) return { state: 'none', days: null, exp: null };
  const end = new Date(`${i.expirationDate}T23:59:59`);
  if (Number.isNaN(end.getTime())) return { state: 'none', days: null, exp: i.expirationDate };
  const days = Math.floor((end.getTime() - ref.getTime()) / 86400000);
  if (days < 0) return { state: 'expired', days, exp: i.expirationDate };
  if (days <= INSURANCE_EXPIRING_DAYS) return { state: 'expiring', days, exp: i.expirationDate };
  return { state: 'current', days, exp: i.expirationDate };
}

/** True only for a policy that is on file AND has not expired. */
export function hasInsuranceOnFile(u: Pick<UserDoc, 'insurance'> | null | undefined, ref: Date = new Date()): boolean {
  const s = insuranceStatus(u, ref).state;
  return s === 'current' || s === 'expiring';
}

/** "2026-11-01" → "Nov 1, 2026" for the owner's cards. */
export function fmtPolicyDate(d: string | null | undefined): string {
  if (!d) return '—';
  const t = new Date(`${d}T12:00:00`);
  return Number.isNaN(t.getTime()) ? d : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Only standard lessees carry this obligation — not the owner, not a checkout CFI. */
export function soloGateApplies(u: Pick<UserDoc, 'role' | 'billingProfile' | 'insurance'> | null | undefined): boolean {
  if (!u || u.role !== 'lessee' || u.billingProfile === 'owner' || u.billingProfile === 'cfi') return false;
  return !hasInsuranceOnFile(u);
}

/**
 * The dashboard notice is dismissible; the booking-screen one is not. Showing it
 * on every screen forever produces banner blindness, which protects nobody — the
 * dated acknowledgment is what answers "I never saw that".
 */
export function showDashboardNotice(u: Pick<UserDoc, 'role' | 'billingProfile' | 'insurance' | 'soloNoticeAckAt'> | null | undefined): boolean {
  return soloGateApplies(u) && !u?.soloNoticeAckAt;
}

/** Days left against the 14-day backstop, counted from the lease effective date. */
export function coiDueInfo(effectiveDate?: string, ref: Date = new Date()) {
  if (!effectiveDate) return null;
  const due = new Date(`${effectiveDate}T12:00:00`);
  due.setDate(due.getDate() + SOLO_GATE_DAYS);
  const dueDate = due.toISOString().slice(0, 10);
  const daysLeft = Math.round((due.getTime() - ref.getTime()) / 86400000);
  return { dueDate, daysLeft, overdue: daysLeft < 0 };
}
